import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import SpecialCard from "@/components/feature/SpecialCard";
import useSeo from "@/hooks/useSeo";
import useFavorites from "@/hooks/useFavorites";
import {
  getMovieById,
  getRelatedMovies,
  getSpecialsByIds,
} from "@/utils/data";

export default function Detail() {
  const { id = "" } = useParams();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useSeo({
    title: movie
      ? `${movie.title} - 百合影视详情`
      : "作品未找到 - 百合影视",
    description: movie
      ? `${movie.synopsis}（${movie.year} · ${movie.region} · ${movie.type}）`
      : "该百合影视作品不存在或已下架。",
    keywords: movie
      ? `${movie.title},百合${movie.type},${movie.tags.join(",")},百合影视详情`
      : "百合影视,百合详情",
  });

  if (!movie) {
    return (
      <div className="pt-24 md:pt-32">
        <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-secondary-100 text-3xl text-foreground-400">
            <i className="ri-file-unknow-line" />
          </span>
          <h1 className="mt-5 font-heading text-xl font-semibold text-foreground-950">
            未找到该作品
          </h1>
          <p className="mt-2 max-w-md text-sm text-foreground-600">
            你访问的影视作品可能已被移除，或者链接有误。回到分类页看看其他精彩内容吧。
          </p>
          <Link
            to="/category"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            返回影视分类
          </Link>
        </div>
      </div>
    );
  }

  const fav = isFavorite(movie.id);
  const related = getRelatedMovies(movie, 4);
  const relatedSpecials = getSpecialsByIds(movie.specialIds);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(url);
        showToast("链接已复制到剪贴板");
      } else {
        showToast(`请手动复制链接：${url}`);
      }
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  const infoItems = [
    { label: "年份", value: `${movie.year} 年` },
    { label: "类型", value: movie.type },
    { label: "地区", value: movie.region },
    { label: "评分", value: movie.rating.toFixed(1) },
  ];

  return (
    <div className="pt-16 md:pt-20">
      <section className="bg-secondary-50">
        <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-12">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: "影视分类", to: "/category" },
              { label: movie.type, to: `/category?type=${movie.type}` },
              { label: movie.title },
            ]}
          />

          <div className="mt-6 grid gap-8 lg:grid-cols-[320px_1fr] lg:gap-12">
            <div className="mx-auto w-[220px] sm:w-[260px] lg:mx-0 lg:w-full">
              <div className="overflow-hidden rounded-lg bg-background-200">
                <div className="aspect-[2/3] w-full">
                  <img
                    src={movie.cover}
                    alt={`${movie.title} 封面`}
                    title={`${movie.title} 百合影视`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
                  {movie.type}
                </span>
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-md bg-accent-100 px-2.5 py-1 text-xs text-accent-900"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
                {movie.title}
              </h1>

              <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-foreground-600">
                <span className="flex items-center gap-1.5 text-accent-600">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-star-fill" />
                  </span>
                  <strong className="text-base text-foreground-950">
                    {movie.rating.toFixed(1)}
                  </strong>
                  <span className="text-foreground-400">/ 10</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-calendar-line" />
                  </span>
                  {movie.year} 年
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-map-pin-line" />
                  </span>
                  {movie.region}
                </span>
              </div>

              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">
                {movie.synopsis}
              </p>

              <div className="mt-6 grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4">
                {infoItems.map((item) => (
                  <div
                    key={item.label}
                    className="rounded-lg border border-background-200/70 bg-background-50 px-3 py-3"
                  >
                    <p className="text-xs text-foreground-400">{item.label}</p>
                    <p className="mt-1 text-sm font-medium text-foreground-900">
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>

              <dl className="mt-6 max-w-2xl space-y-2 text-sm">
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-400">导演</dt>
                  <dd className="text-foreground-800">{movie.director}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-16 shrink-0 text-foreground-400">主演</dt>
                  <dd className="text-foreground-800">
                    {movie.cast.join(" / ")}
                  </dd>
                </div>
              </dl>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(movie.id)}
                  className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md px-6 py-3 text-sm font-medium transition-colors ${
                    fav
                      ? "bg-accent-500 text-background-50"
                      : "bg-primary-500 text-background-50 hover:bg-primary-600"
                  }`}
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i
                      className={fav ? "ri-heart-3-fill" : "ri-heart-3-line"}
                    />
                  </span>
                  {fav ? "已收藏" : "收藏作品"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/70 bg-background-50 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-share-line" />
                  </span>
                  分享
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-12 md:px-6 md:py-16">
        <div className="grid gap-10 lg:grid-cols-[1fr_320px] lg:gap-14">
          <article>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              剧情简介
            </h2>
            <div className="mt-5 space-y-4">
              {movie.description.map((paragraph) => (
                <p
                  key={paragraph.slice(0, 12)}
                  className="text-sm leading-loose text-foreground-700 md:text-base"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-400">所属分类：</span>
              <Link
                to={`/category?type=${movie.type}`}
                className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-secondary-200"
              >
                <i className="ri-price-tag-3-line" />
                百合{movie.type}
              </Link>
              <Link
                to={`/category?region=${movie.region}`}
                className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-secondary-200"
              >
                <i className="ri-map-pin-line" />
                {movie.region}
              </Link>
              <Link
                to={`/category?year=${movie.year}`}
                className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-3 py-1 text-xs text-foreground-700 transition-colors hover:bg-secondary-200"
              >
                <i className="ri-calendar-line" />
                {movie.year} 年
              </Link>
            </div>
          </article>

          <aside>
            {relatedSpecials.length > 0 ? (
              <div>
                <h2 className="font-heading text-lg font-bold text-foreground-950">
                  收录于专题
                </h2>
                <div className="mt-4 space-y-4">
                  {relatedSpecials.map((special) => (
                    <SpecialCard
                      key={special.id}
                      special={special}
                      compact
                    />
                  ))}
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-background-200/70 bg-background-50 p-5">
                <h2 className="font-heading text-base font-semibold text-foreground-950">
                  关于本站
                </h2>
                <p className="mt-3 text-xs leading-relaxed text-foreground-600">
                  本站为百合影视爱好者提供作品推荐与资讯浏览服务，内容均为虚构演示数据，不含真实播放与下载功能。
                </p>
              </div>
            )}
          </aside>
        </div>
      </section>

      <section className="bg-secondary-50 py-12 md:py-16">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <div className="flex items-end justify-between gap-4">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                相关推荐
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                喜欢《{movie.title}》的你，可能也会喜欢这些
              </p>
            </div>
            <Link
              to="/category"
              className="hidden shrink-0 items-center gap-1 whitespace-nowrap text-sm text-primary-600 transition-colors hover:text-primary-700 sm:inline-flex"
            >
              更多作品
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-line" />
              </span>
            </Link>
          </div>

          <div className="mt-8 grid grid-cols-2 gap-x-5 gap-y-8 sm:grid-cols-3 lg:grid-cols-4">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 rounded-md bg-foreground-950 px-4 py-2.5 text-sm text-background-50 shadow-lg animate-fade-in">
          {toast}
        </div>
      )}
    </div>
  );
}